import type { PatientWorkspaceSummaryResponseV1 } from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import { Pressable, ScrollView, Text, View } from "react-native";

import { styles } from "../AppShell.styles.js";
import { renderLegacyPreviewListRow } from "../shell-legacy-ui.js";
import {
  renderLegacySurfaceHeader,
  renderWorkbenchActionButton,
} from "../shell-primitives.js";
import type { AppShellTheme } from "../shell-core.js";
import type { PatientWorkspaceSummarySurfaceRow } from "./patient-workspace-builders.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

type PatientWorkspaceTabId =
  | "overview"
  | "onboarding"
  | "appointments"
  | "procedures"
  | "medications"
  | "documents"
  | "calls"
  | "messages"
  | "settings";

export type PatientWorkspaceNavigationItem = {
  id: PatientWorkspaceTabId;
  label: string;
  badgeText: string | null;
};

export function renderPatientWorkspaceQuickActionBand(input: {
  enabledTabIds: readonly PatientWorkspaceTabId[];
  isMediumLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  onRequestCallback: () => void;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  setSelectedPatientWorkspaceTabId: (value: PatientWorkspaceTabId) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    enabledTabIds,
    isMediumLayout,
    layout,
    onRequestCallback,
    selectedPatientWorkspaceTabId,
    setSelectedPatientWorkspaceTabId,
    summary,
    theme,
    tokens,
  } = input;
  const enabledTabIdSet = new Set<PatientWorkspaceTabId>(enabledTabIds);
  const agentIsOpen = selectedPatientWorkspaceTabId === "messages";
  const onboardingIsOpen = selectedPatientWorkspaceTabId === "onboarding";
  const selectedTabStatusText =
    selectedPatientWorkspaceTabId === "messages"
      ? "Agent workspace is active below."
      : selectedPatientWorkspaceTabId === "documents"
        ? "Document workspace is active below."
        : selectedPatientWorkspaceTabId === "calls"
          ? "Phone calls workspace is active below."
          : selectedPatientWorkspaceTabId === "appointments"
            ? "Appointment workspace is active below."
            : selectedPatientWorkspaceTabId === "medications"
              ? "Medication workspace is active below."
              : selectedPatientWorkspaceTabId === "onboarding"
                ? "Onboarding workspace is active below."
                : selectedPatientWorkspaceTabId === "settings"
                  ? "Profile is active below."
                  : "Overview home is active below.";
  const selfServiceActions = [
    {
      id: "quick-agent",
      requiredTabId: "messages" as const,
      title: `Launch ${brand.assistantName}`,
      subtitle:
        (summary?.unreadMessageCount ?? 0) > 0
          ? `${summary?.unreadMessageCount ?? 0} unread updates waiting`
          : "Open the patient conversation and ask a question",
      label: agentIsOpen ? "Return home" : "Open messages",
      onPress: () =>
        setSelectedPatientWorkspaceTabId(agentIsOpen ? "overview" : "messages"),
      tone: "accent" as const,
    },
    {
      id: "quick-documents",
      requiredTabId: "documents" as const,
      title: "Review documents",
      subtitle:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? `${summary?.pendingDocumentCount ?? 0} records still pending`
          : "All currently available records are ready to review",
      label: "Open documents",
      onPress: () => setSelectedPatientWorkspaceTabId("documents"),
      tone: "success" as const,
    },
    {
      id: "quick-appointments",
      requiredTabId: "appointments" as const,
      title: "Check appointments",
      subtitle: summary?.nextAppointmentText?.trim().length
        ? summary.nextAppointmentText
        : "No upcoming appointment scheduled yet",
      label: "Open appointments",
      onPress: () => setSelectedPatientWorkspaceTabId("appointments"),
      tone: "accent" as const,
    },
    {
      id: "quick-medications",
      requiredTabId: "medications" as const,
      title: "Medication reminders",
      subtitle: summary?.medicationSummaryText?.trim().length
        ? summary.medicationSummaryText
        : "Medication guidance is not available yet",
      label: "Open medications",
      onPress: () => setSelectedPatientWorkspaceTabId("medications"),
      tone: "success" as const,
    },
  ].filter((action) => enabledTabIdSet.has(action.requiredTabId));
  const portalSummaryItems = [
    {
      id: "summary-unread",
      requiredTabId: "messages" as const,
      labelText: "Agent updates",
      valueText: String(summary?.unreadMessageCount ?? 0),
      toneColor: theme.info,
    },
    {
      id: "summary-documents",
      requiredTabId: "documents" as const,
      labelText: "Pending docs",
      valueText: String(summary?.pendingDocumentCount ?? 0),
      toneColor: theme.warning,
    },
    {
      id: "summary-appointment",
      requiredTabId: "appointments" as const,
      labelText: "Next visit",
      valueText: summary?.nextAppointmentText?.trim().length
        ? summary.nextAppointmentText
        : "Not scheduled",
      toneColor: theme.accentStrong,
    },
    {
      id: "summary-procedure",
      requiredTabId: "procedures" as const,
      labelText: "Procedure",
      valueText: summary?.procedureSummaryText?.trim().length
        ? summary.procedureSummaryText
        : "No procedure on file",
      toneColor: theme.textPrimary,
    },
  ].filter((item) => enabledTabIdSet.has(item.requiredTabId));

  return (
    <View
      style={[
        styles.patientQuickActionBand,
        {
          backgroundColor: theme.accentSubtle,
          borderColor: theme.borderStrong,
          borderRadius: layout.cardRadius,
          gap: tokens.spacing.sm,
          padding: tokens.spacing.md,
        },
      ]}
    >
      <Text
        style={[
          styles.navigationText,
          {
            color: theme.accentStrong,
            fontFamily: layout.displayFontFamily,
            fontSize: tokens.typography.fontSize.md,
            lineHeight: tokens.typography.lineHeight.md,
            textAlign: "center",
          },
        ]}
      >
        Self-service hub
      </Text>

      <View
        style={[
          styles.patientMetaGrid,
          {
            gap: tokens.spacing.md,
          },
        ]}
      >
        {portalSummaryItems.map((item) => (
          <View
            key={item.id}
            style={[
              styles.patientMetaCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.xs,
                minHeight:
                  item.id === "summary-appointment" ||
                  item.id === "summary-procedure"
                    ? 88
                    : 76,
                padding: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              {item.labelText}
            </Text>
            <Text
              style={[
                styles.navigationText,
                {
                  color: item.toneColor,
                  fontFamily: layout.displayFontFamily,
                  fontSize:
                    item.id === "summary-appointment" ||
                    item.id === "summary-procedure"
                      ? tokens.typography.fontSize.sm
                      : tokens.typography.fontSize.lg,
                  lineHeight:
                    item.id === "summary-appointment" ||
                    item.id === "summary-procedure"
                      ? tokens.typography.lineHeight.sm
                      : tokens.typography.lineHeight.lg,
                },
              ]}
            >
              {item.valueText}
            </Text>
          </View>
        ))}
      </View>

      <View
        style={[
          styles.patientQuickActionRow,
          {
            alignItems: isMediumLayout ? "stretch" : "stretch",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        {selfServiceActions.map((action) => (
          <View
            key={action.id}
            style={[
              styles.detailSectionCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                },
              ]}
            >
              {action.title}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {action.subtitle}
            </Text>
            {renderWorkbenchActionButton({
              disabled: false,
              label: action.label,
              onPress: action.onPress,
              size: "compact",
              theme,
              tone: action.tone,
              tokens,
            })}
          </View>
        ))}
      </View>

      <View
        style={[
          styles.patientRowFooter,
          {
            gap: tokens.spacing.xs,
            justifyContent: "center",
          },
        ]}
      >
        {renderWorkbenchActionButton({
          disabled: false,
          label: onboardingIsOpen ? "Return home" : "Open onboarding",
          onPress: () =>
            setSelectedPatientWorkspaceTabId(
              onboardingIsOpen ? "overview" : "onboarding",
            ),
          size: "compact",
          theme,
          tone: "accent",
          tokens,
        })}
        {renderWorkbenchActionButton({
          disabled: false,
          label: "Request callback",
          onPress: onRequestCallback,
          size: "compact",
          theme,
          tone: "success",
          tokens,
        })}
      </View>

      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            textAlign: "center",
          },
        ]}
      >
        {selectedTabStatusText}
      </Text>
    </View>
  );
}

export function renderPatientWorkspaceNavigationBar(input: {
  isMediumLayout: boolean;
  items: readonly PatientWorkspaceNavigationItem[];
  layout: typeof designTokens.semanticLayout;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  setSelectedPatientWorkspaceTabId: (value: PatientWorkspaceTabId) => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    isMediumLayout,
    items,
    layout,
    selectedPatientWorkspaceTabId,
    setSelectedPatientWorkspaceTabId,
    theme,
    tokens,
  } = input;

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[
        styles.patientNavBar,
        {
          gap: tokens.spacing.xs,
          paddingBottom: tokens.spacing.xs,
        },
      ]}
    >
      {items.map((item) => {
        const isSelected = item.id === selectedPatientWorkspaceTabId;

        return (
          <Pressable
            key={item.id}
            onPress={() => setSelectedPatientWorkspaceTabId(item.id)}
            style={[
              styles.patientNavButton,
              {
                backgroundColor: isSelected
                  ? theme.surface
                  : theme.accentSubtle,
                borderColor: isSelected ? theme.surface : theme.borderStrong,
                borderTopLeftRadius: layout.controlRadius,
                borderTopRightRadius: layout.controlRadius,
                gap: tokens.spacing.sm,
                minWidth: isMediumLayout ? 172 : 148,
                paddingHorizontal: tokens.spacing.lg,
                paddingVertical: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: isSelected ? "700" : "600",
                },
              ]}
            >
              {item.label}
            </Text>

            {item.badgeText ? (
              <View
                style={[
                  styles.patientNavBadge,
                  {
                    backgroundColor: isSelected
                      ? theme.accentSubtle
                      : theme.surface,
                    borderColor: theme.borderSubtle,
                    borderRadius: tokens.radius.pill,
                    paddingHorizontal: tokens.spacing.sm,
                    paddingVertical: tokens.spacing.xs,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  {item.badgeText}
                </Text>
              </View>
            ) : null}
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function renderPatientWorkspaceSummarySurface(input: {
  eyebrowText?: string | null;
  title: string;
  subtitle?: string | null;
  rows: readonly PatientWorkspaceSummarySurfaceRow[];
  emptyStateText: string;
  layout: typeof designTokens.semanticLayout;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    eyebrowText = null,
    title,
    subtitle = null,
    rows,
    emptyStateText,
    layout,
    theme,
    tokens,
  } = input;

  return (
    <View
      style={[
        styles.surfaceCard,
        {
          backgroundColor: theme.surfaceElevated,
          borderColor: theme.borderStrong,
          borderRadius: layout.cardRadius,
          gap: tokens.spacing.md,
          padding: tokens.spacing.lg,
          width: "100%",
        },
      ]}
    >
      {renderLegacySurfaceHeader({
        eyebrowText,
        title,
        subtitle,
        layout,
        theme,
        tokens,
      })}

      <View
        style={[
          styles.listGroup,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {rows.length > 0 ? (
          rows.map((row) =>
            renderLegacyPreviewListRow({
              id: row.id,
              title: row.title,
              subtitle: row.subtitle,
              supportingText: row.supportingText,
              badgeText: row.badgeText,
              leadPrimaryText: row.leadPrimaryText,
              leadSecondaryText: row.leadSecondaryText,
              tone: row.tone,
              layout,
              theme,
              tokens,
            }),
          )
        ) : (
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceMuted,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {emptyStateText}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}
